const CURRENCY_SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

/**
 * True when a deposit was asked for but has NOT been paid. Includes older staff
 * deposit-link bookings that have no deposit_status at all.
 */
export function isDepositUnpaid(booking) {
  if (!booking?.deposit_required) return false;
  return !['paid', 'refunded', 'none'].includes(booking.deposit_status || 'pending');
}

export function formatDepositAmount(booking) {
  const symbol = CURRENCY_SYMBOLS[booking?.deposit_currency] || '£';
  return `${symbol}${Number(booking?.deposit_amount || 0).toFixed(2)}`;
}

/** Ask staff to confirm before marking an unpaid-deposit guest as arrived/seated. */
export function confirmUnpaidDepositArrival(booking, nextStatus) {
  if (!isDepositUnpaid(booking) || !['arrived', 'seated'].includes(nextStatus)) return true;
  return window.confirm(
    `⚠️ DEPOSIT NOT PAID\n\n${booking.customer_name || 'This guest'} has NOT paid their ${formatDepositAmount(booking)} deposit.\n` +
      'Do not deduct a deposit from their bill.\n\nContinue and mark them as ' + nextStatus + '?'
  );
}

/**
 * Warn staff before cancelling a booking with a paid deposit: if it's still within the
 * refund window, cancelling will automatically issue a Stripe refund. Return false to
 * abort the cancellation. The server makes the final refund-eligibility call either way.
 */
export function confirmDepositRefundOnCancel(booking, nextStatus) {
  if (nextStatus !== 'cancelled' || booking?.deposit_status !== 'paid') return true;
  if (booking.deposit_locked) {
    return window.confirm(
      `⚠️ DEPOSIT LOCKED\n\n${booking.customer_name || 'This guest'}'s ${formatDepositAmount(booking)} deposit is locked and will NOT be auto-refunded.\n` +
        'If a refund is still owed, contact a supervisor to process it manually.\n\nContinue cancelling this booking?'
    );
  }
  return window.confirm(
    `⚠️ PAID DEPOSIT\n\n${booking.customer_name || 'This guest'} paid a ${formatDepositAmount(booking)} deposit.\n` +
      'Cancelling now will automatically issue a refund via Stripe (if still within the refund window).\n\n' +
      'If you are unsure, stop and contact a supervisor.\n\nContinue and cancel this booking?'
  );
}

/** Compare booking state before/after a cancel to describe what happened to its deposit. */
export function describeCancelDepositOutcome(before, after) {
  if (!before || before.deposit_status !== 'paid') return null;
  if (after?.deposit_status === 'refunded') {
    return `Deposit refunded: ${formatDepositAmount({ ...before, deposit_amount: after.deposit_refund_amount ?? before.deposit_amount })}`;
  }
  return 'Deposit was NOT refunded (outside the refund window, locked, or Stripe could not be reached). Contact a supervisor if this needs manual review.';
}
